import dayjs from 'dayjs';
import { Alert, Text } from '@kula-ai/components';
import { Box, Flex } from '@chakra-ui/react';
import { useDateFormat } from '@/hooks/useDateFormat';
import type { ContractEnded } from './types';

export interface ContractEndedNoticeProps {
  contractEnded: ContractEnded;
}

const Row = ({ label, value }: { label: string; value: string }) => (
  <Flex gap="3">
    <Box minW="60px">
      <Text variant="small" color="muted">
        {label}
      </Text>
    </Box>
    <Text variant="small">{value}</Text>
  </Flex>
);

/**
 * A conversion that stopped half-way.
 *
 * The contract is ended and the hire is not, so the person is neither
 * contractor nor employee. The form no longer asks for the end-contract
 * details — the backend drops that section — and without this the recruiter
 * would see an ordinary hire form with no sign that half of it already
 * happened, and no way to tell which half.
 */
export const ContractEndedNotice = ({
  contractEnded,
}: ContractEndedNoticeProps) => {
  const { dateFormat } = useDateFormat();
  const endedOn = contractEnded.contract_end_date
    ? dayjs(contractEnded.contract_end_date).format(dateFormat.label)
    : undefined;

  return (
    <Alert
      status="warning"
      title="Contract already ended"
      description="Only the hire is outstanding."
      data-test-id="workday-contract-ended-notice"
    >
      <Flex direction="column" gap="1" mt="2">
        {contractEnded.contingent_worker_id && (
          <Row label="Worker" value={contractEnded.contingent_worker_id} />
        )}
        {endedOn && <Row label="Ended" value={endedOn} />}
      </Flex>
    </Alert>
  );
};

export default ContractEndedNotice;
